import React from "react";
import MaterialIcon from "../../../../components/BuilderComponents/MaterialIcon";
import Button from "../../../../components/FormComponents/Button";
import { getStatusConfig } from "../../config";
import ActivityCell from "./ActivityCell";
import SummaryCell from "./SummaryCell";
import ApplicationCell from "./ApplicationCell";

const ReleaseRow = ({ item, onOpenModal, onCopyId, onPromote }) => {
  const { release_request, promote_request } = item;

  const textTruncateStyle = {
    maxWidth: "160px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
  };

  // Define helper to check valid date
  const isInvalidDate = (date) => !date || date === "0001-01-01T00:00:00Z";

  // Helper to determine if a request exists
  const hasState = (req) => req && req.state && req.state !== "";

  const renderRow = (requestType, requestData, rowError, requestId, icon) => {
    const status = requestData?.state || "";
    const config = getStatusConfig(status);

    const summaryItem = {
      ...item,
      initiater_name: requestData?.initiator_name || item.initiater_name,
      updated_at: !isInvalidDate(requestData?.updated_at) ? requestData.updated_at : item.updated_at
    };

    const canApproveReject = ["INITIATED", "PENDING", "AWAITING_APPROVAL", "AWAITING_PROMOTION"].includes(status);
    const promoteStatus = promote_request?.state;
    // Check if promotion is active to disable actions on Release row
    const canPromote = status === "RELEASED" && promoteStatus === "";

    let reason = "";
    if (requestType === "Promote") {
      reason = item.promote_request.reason || item?.reason || "";
    } else {
      reason = item.release_request.reason || item?.reason || "";
    }
    return (
      <div key={`${item.id}-${requestType}`} className="d_flex align_center p_2 hover_bg_gray transition gap_2">
        {/* Summary (30%) */}
        <div className="w_30 d_flex flex_column gap_1 mb_2" style={{ overflow: "hidden" }}>
          <SummaryCell
            icon={icon}
            requestId={requestId}
            item={summaryItem}
            onCopyId={onCopyId}
            requestType={requestType}
            reason={reason}
          />
        </div>

        {/* Application (20%) */}
        <div className="w_20 d_flex align_center">
          <ApplicationCell item={item} onCopyId={onCopyId} />
        </div>

        {/* Version (10%) */}
        <div className="w_10 d_flex align_center">
          {item?.version ? (
            <span
              className="font_weight_medium d_flex align_center"
              style={{
                backgroundColor: "#F3F4F6", // Light gray background
                color: "#374151", // Dark gray text
                padding: "2px 8px", // Pill padding
                borderRadius: "12px", // Fully rounded corners
                fontSize: "11px",
                fontFamily: "monospace", // Monospace for technical data
                border: "1px solid #E5E7EB", // Subtle border for definition
                letterSpacing: "-0.5px"
              }}
            >
              v{item.version}
            </span>
          ) : (
            <span className="font_size_xsmall color_disabled font_style_italic">--</span>
          )}
        </div>

        {/* Status (15%) */}
        <div className="w_15 d_flex align_center">
          <span
            className="d_flex align_center gap_1 font_weight_medium px_2 py_1 border_radius_small w_fit_content"
            style={{
              backgroundColor: config.bg,
              color: config.text,
              fontSize: "12px",
              lineHeight: "1.2",
              borderRadius: "4px",
              ...textTruncateStyle,
              maxWidth: "90%"
            }}
          >
            {config.icon && <MaterialIcon name={config.icon} style={{ fontSize: "12px", color: config.icon }} />}
            <span style={{ paddingTop: "1px" }}>{status}</span>
          </span>
        </div>

        {/* Activity (10%) */}
        <div className="w_10 d_flex align_center font_size_small">
          <ActivityCell actions={requestData?.actions || []} />
        </div>

        {/* Actions (15%) - Includes Error Handling */}
        <div className="w_15 d_flex align_center font_size_small">
          {rowError ? (
            <div className="d_flex align_center font_style_italic color_disabled w_100">
              <MaterialIcon name="warning" style={{ fontSize: "20px", color: "#d92d20", flexShrink: 0 }} />
              <span
                className="ml_1 font_size_xsmall"
                style={{
                  maxWidth: "90%",
                  maxHeight: "50px",
                  display: "inline-block",
                  overflow: "scroll",
                  whiteSpace: "wrap"
                }}
              >
                {rowError}
              </span>
            </div>
          ) : (
            <div className="d_flex gap_2 align_center">
              {canApproveReject && (
                <>
                  <Button variant="plain" className="d_flex gap_1" onClick={() => onOpenModal(item.id, "APPROVE")}>
                    <MaterialIcon name="check_circle" style={{ color: "#34a853" }} />
                    Approve
                  </Button>
                  <Button variant="plain" className="d_flex gap_1" onClick={() => onOpenModal(item.id, "REJECT")}>
                    <MaterialIcon name="cancel" style={{ color: "#d92d20" }} />
                    Reject
                  </Button>
                </>
              )}

              {canPromote && (
                <Button variant="plain" className="d_flex gap_1" onClick={() => onPromote(item.id)}>
                  <MaterialIcon name="check" style={{ color: "#34a853" }} />
                  Promote
                </Button>
              )}

              {!canApproveReject && !canPromote && <div className="font_style_italic color_disabled">No actions</div>}
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Logic for Error Attribution ---

  const globalError = item.error || "";
  const promoteActive = hasState(promote_request);
  const releaseActive = hasState(release_request);

  const promoteErrorMsg = promoteActive && globalError ? globalError : "";
  const releaseErrorMsg = releaseActive && !promoteActive && globalError ? globalError : "";

  return (
    <div
      className="d_flex flex_column border_radius_medium"
      style={{
        backgroundColor: "#FFFFFF",
        border: "1px solid #DFE1E6",
        overflow: "hidden"
      }}
    >
      <div className="d_flex flex_column">
        {promoteActive && renderRow("Promote", promote_request, promoteErrorMsg, item.id, "rocket_launch")}
        {releaseActive && renderRow("Release", release_request, releaseErrorMsg, item.id, "trending_up")}

        {!promoteActive && !releaseActive && (
          <div className="p_4 text_center color_secondary font_style_italic">No activity found</div>
        )}
      </div>
    </div>
  );
};

export default ReleaseRow;
